import { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import { ArrowUp } from "lucide-react";

export default function ScrollProgress() {
  const [showButton, setShowButton] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressCircleRef = useRef<SVGCircleElement>(null);

  // SVG circle specifications for the progress ring
  const radius = 15;
  const strokeWidth = 2.5;
  const circumference = radius * 2 * Math.PI;

  useEffect(() => {
    let ticking = false;
    let cachedMaxScroll = 1;

    const updateDimensions = () => {
      const scrollHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        1
      );
      const clientHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      cachedMaxScroll = Math.max(scrollHeight - clientHeight, 1);
    };

    const updateScroll = () => {
      const scrollTop = Math.max(window.scrollY || document.documentElement.scrollTop || 0, 0);
      
      // Calculate progress based on cached max scroll height (no layout reflow during scroll)
      let progress = scrollTop / cachedMaxScroll;
      
      // When at or near the very bottom (footer), snap smoothly to 100% complete
      if (scrollTop + (window.innerHeight || 1) >= (document.documentElement.scrollHeight || 1) - 10) {
        progress = 1;
      }
      progress = Math.min(Math.max(progress, 0), 1);
      
      // 1. Update top bar using GPU-accelerated scaleX
      if (progressBarRef.current) {
        progressBarRef.current.style.transform = `scaleX(${progress})`;
      }

      // 2. Update radial progress ring strokeDashoffset
      if (progressCircleRef.current) {
        const strokeDashoffset = circumference - progress * circumference;
        progressCircleRef.current.style.strokeDashoffset = `${strokeDashoffset}`;
      }

      // 3. Optimize state updates: only set state when threshold is crossed to prevent redundant re-renders
      const shouldShow = scrollTop > 200;
      setShowButton((prev) => (prev !== shouldShow ? shouldShow : prev));

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateDimensions, { passive: true });

    // Initial dimension & scroll calculation
    updateDimensions();
    updateScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateDimensions);
    };
  }, [circumference]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      {/* Global Reading Progress Bar - GPU Accelerated via scaleX */}
      <div 
        className="fixed top-0 left-0 right-0 h-[4px] z-50 bg-emerald-950/5 pointer-events-none"
        style={{ transform: "translateZ(0)" }}
      >
        <div
          ref={progressBarRef}
          className="h-full bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-400 relative origin-left shadow-[0_1.5px_12px_rgba(16,185,129,0.5)]"
          style={{ transform: "scaleX(0)", transformOrigin: "left", willChange: "transform" }}
        >
          {/* Shimmering Animated Lead Flare for high-fidelity visual appeal */}
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-r from-transparent via-white/60 to-white blur-[1.5px] rounded-r-full" />
        </div>
      </div>

      {/* Dynamic Circular Back-to-Top Tracker */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7, y: 20, pointerEvents: "none" }}
        animate={{ 
          opacity: showButton ? 1 : 0, 
          scale: showButton ? 1 : 0.7, 
          y: showButton ? 0 : 20,
          pointerEvents: showButton ? "auto" : "none"
        }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="fixed bottom-6 right-6 z-40"
      >
        <button
          onClick={scrollToTop}
          className="relative flex items-center justify-center w-12 h-12 rounded-full bg-white text-emerald-600 shadow-[0_6px_24px_rgba(16,185,129,0.18)] hover:shadow-[0_8px_32px_rgba(16,185,129,0.32)] transition-all duration-300 group cursor-pointer focus:outline-hidden border border-emerald-50/20"
          aria-label="Scroll back to top"
          id="back-to-top-tracker"
        >
          {/* SVG Radial Progress Ring Background */}
          <svg className="absolute top-0 left-0 w-full h-full -rotate-90 pointer-events-none" viewBox="0 0 36 36">
            <defs>
              <linearGradient id="ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#059669" /> {/* emerald-600 */}
                <stop offset="50%" stopColor="#10b981" /> {/* emerald-500 */}
                <stop offset="100%" stopColor="#14b8a6" /> {/* teal-500 */}
              </linearGradient>
            </defs>
            
            {/* Static light background track */}
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="transparent"
              stroke="#f3f4f6"
              strokeWidth={strokeWidth}
              className="opacity-90"
            />
            
            {/* Active progress ring colored with brand gradient */}
            <circle
              ref={progressCircleRef}
              cx="18"
              cy="18"
              r={radius}
              fill="transparent"
              stroke="url(#ring-gradient)"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={circumference}
              strokeLinecap="round"
              style={{ willChange: "stroke-dashoffset" }}
            />
          </svg>

          {/* Central Arrow Icon with tactile scroll animation */}
          <motion.div
            className="relative z-10 text-emerald-600 group-hover:text-emerald-700"
            whileHover={{ y: -3, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <ArrowUp className="w-5 h-5 stroke-[2.5]" />
          </motion.div>

          {/* Pulse ripple backdrop on hover */}
          <div className="absolute inset-0 rounded-full bg-emerald-500/0 group-hover:bg-emerald-500/5 transition-colors duration-300 pointer-events-none" />
        </button>
      </motion.div>
    </>
  );
}
